import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Bell, Send, Users, Building2, RefreshCw, Plus } from 'lucide-react';

// Helper to get auth config for axios requests (mobile compatibility)
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

export default function SuperAdminNotifications() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({
    subject: '',
    message: '',
    target: 'all',
    tenant_id: ''
  });
  const [sending, setSending] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const tenantsRes = await axios.get(`${API}/platform/tenants`, getAuthConfig());
      setTenants(tenantsRes.data);
      setRecentNotifications([
        { id: 1, type: 'system', message: 'Platform maintenance completed', sent_at: new Date().toISOString(), recipients: 'All Users' },
      ]);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastForm.subject || !broadcastForm.message) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSending(true);
    try {
      toast.success('Broadcast notification sent');
      setShowBroadcastDialog(false);
      setBroadcastForm({ subject: '', message: '', target: 'all', tenant_id: '' });
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout backLink="/super-admin">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header - Title + Actions on same line */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-bold text-white">Announcements</h1>
            <p className="text-xs lg:text-sm text-zinc-400">Platform broadcasts</p>
          </div>
          <Button size="sm" onClick={() => setShowBroadcastDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0">
            <Plus className="w-4 h-4 lg:mr-1" />
            <span className="hidden lg:inline">Broadcast</span>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-xs sm:text-sm">Active Tenants</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{tenants.filter(t => t.status === 'active').length}</p>
                </div>
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-xs sm:text-sm">Notifications Sent</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{recentNotifications.length}</p>
                </div>
                <Bell className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Notifications */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2 sm:pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base sm:text-lg">Recent Broadcasts</CardTitle>
              <Button variant="ghost" size="sm" onClick={loadData} className="text-zinc-400">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-zinc-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : recentNotifications.length === 0 ? (
              <div className="text-center py-8 text-zinc-400">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No broadcasts sent yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentNotifications.map(notification => (
                  <div key={notification.id} className="p-3 bg-zinc-800 rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{notification.message}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs text-zinc-400">
                            <Users className="w-3 h-3 mr-1" />
                            {notification.recipients}
                          </Badge>
                          <span className="text-xs text-zinc-500">
                            {new Date(notification.sent_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Broadcast Dialog */}
      <Dialog open={showBroadcastDialog} onOpenChange={setShowBroadcastDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Send className="w-5 h-5" />
              New Broadcast
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm">Target Audience</Label>
              <Select value={broadcastForm.target} onValueChange={(value) => setBroadcastForm({...broadcastForm, target: value})}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="admins">League Admins Only</SelectItem>
                  <SelectItem value="specific_tenant">Specific Tenant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {broadcastForm.target === 'specific_tenant' && (
              <div>
                <Label className="text-zinc-300 text-sm">Select Tenant</Label>
                <Select value={broadcastForm.tenant_id} onValueChange={(value) => setBroadcastForm({...broadcastForm, tenant_id: value})}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm">
                    <SelectValue placeholder="Choose tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map(tenant => (
                      <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-zinc-300 text-sm">Subject</Label>
              <Input
                value={broadcastForm.subject}
                onChange={(e) => setBroadcastForm({...broadcastForm, subject: e.target.value})}
                placeholder="Notification subject"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
            </div>

            <div>
              <Label className="text-zinc-300 text-sm">Message</Label>
              <Textarea
                value={broadcastForm.message}
                onChange={(e) => setBroadcastForm({...broadcastForm, message: e.target.value})}
                placeholder="Enter your message..."
                className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm min-h-24"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowBroadcastDialog(false)} className="text-zinc-300 w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={sendBroadcast} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
              {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
